import { createContext, useContext, useState, useCallback, useEffect, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import lazyWithReload from '../../utils/lazyWithReload';
import DrawerLoadingFallback from '../common/DrawerLoadingFallback';
const TaskDrawer = lazyWithReload(() => import('./TaskDrawer'), 'TaskDrawer');
import { sid } from '../../utils/shortId';
import { registerEntityOpener } from '../../utils/openEntity';
import { noProviderOpener } from '../../utils/drawerNoProvider';

/**
 * TaskDrawerContext — URL-driven global task drawer.
 * Source of truth: query param `?taskId=<short>`. Khi taskId đổi → drawer mở/đóng.
 * Trước đây dùng useState + 2 useEffect sync với URL → flip-flop loop khi vào subtask.
 */
const Ctx = createContext(null);

// Provider không nằm trên cây React của người gọi (hay gặp nhất: nội dung xem TRONG một
// drawer KHÁC) → lui về sổ đăng ký module-scope. Xem utils/drawerNoProvider.
const NO_PROVIDER = {
  openTask: noProviderOpener('task'),
  closeTask: () => {},
  version: 0,
  bumpVersion: () => {},
  projectsVersion: 0,
  bumpProjectsVersion: () => {},
  openTaskId: null,
};

export function TaskDrawerProvider({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const taskId = searchParams.get('taskId');
  const [version, setVersion] = useState(0);
  // Separate signal cho thay đổi cấu trúc project/tasklist (rename/add/delete) — tránh refetch projects
  // mỗi khi task complete (gây giật sidebar).
  const [projectsVersion, setProjectsVersion] = useState(0);

  const openTask = useCallback((id) => {
    if (!id) return;
    const short = sid(id);
    setSearchParams(prev => {
      if (prev.get('taskId') === short) return prev; // tránh history.replaceState thừa
      const next = new URLSearchParams(prev);
      next.set('taskId', short);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const closeTask = useCallback(() => {
    setSearchParams(prev => {
      if (!prev.get('taskId')) return prev;
      const next = new URLSearchParams(prev);
      next.delete('taskId');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const bumpVersion = useCallback(() => setVersion(v => v + 1), []);

  // Nạp sẵn chunk drawer ngay khi provider mount, KHÔNG chờ prefetch nền tới lượt: nó xếp
  // theo thứ tự khai báo và có cả trăm route phía trước, nên bấm mở việc trong ~20 giây đầu
  // là ngồi nhìn ô trống (đo trên prod 27/08/2026). Đây là popup được bấm nhiều nhất ở mọi
  // màn có việc — đáng ưu tiên hơn một trang mà người dùng có thể không bao giờ mở.
  useEffect(() => { TaskDrawer.preload?.(); }, []);

  // Khai báo với kernel utils/openEntity: "loại này mở bằng POPUP" → link/card trong chat
  // trỏ tới bản ghi loại này bung popup tại chỗ, không nhảy trang chi tiết (trắng trang chờ chunk).
  useEffect(() => registerEntityOpener('task', openTask), [openTask]);

  const bumpProjectsVersion = useCallback(() => setProjectsVersion(v => v + 1), []);

  return (
    <Ctx.Provider value={{ openTask, closeTask, version, bumpVersion, projectsVersion, bumpProjectsVersion, openTaskId: taskId }}>
      {children}
      {taskId && (
        <Suspense fallback={<DrawerLoadingFallback onClose={closeTask} />}>
          <TaskDrawer
        open={!!taskId}
        taskId={taskId}
        onClose={closeTask}
        onChange={bumpVersion}
      />
        </Suspense>
      )}
    </Ctx.Provider>
  );
}

export function useTaskDrawer() {
  return useContext(Ctx) || NO_PROVIDER;
}
